import { gql } from '@apollo/client'
import { useApolloClient, useMutation } from '@apollo/client/react'
import BookOnlineIcon from '@mui/icons-material/BookOnline'
import BugReportIcon from '@mui/icons-material/BugReport'
import BusinessIcon from '@mui/icons-material/Business'
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth'
import DashboardIcon from '@mui/icons-material/Dashboard'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import PeopleIcon from '@mui/icons-material/People'
import PersonIcon from '@mui/icons-material/Person'
import PlaceIcon from '@mui/icons-material/Place'
import SettingsIcon from '@mui/icons-material/Settings'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import {
  AppBar,
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Toolbar,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { getAssetUrl, getImageUrl } from './config'
import { SiteRole, UserLogoutDocument } from './gql/graphql'
import { useCurrentUser, useSiteContext } from './hooks'

gql`
  mutation UserLogout {
    logout {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

const drawerWidth = 180
const miniDrawerWidth = 64

interface MenuItemDef {
  text: string
  icon: React.ReactNode
  path: string
  superAdminOnly?: boolean
  devOnly?: boolean
}

const menuItems: MenuItemDef[] = [
  { text: '数据看板', icon: <DashboardIcon />, path: '/dashboard' },
  { text: '场地管理', icon: <BookOnlineIcon />, path: '/slots' },
  { text: '订单管理', icon: <ShoppingCartIcon />, path: '/orders' },
  { text: '时段模板', icon: <CalendarViewMonthIcon />, path: '/slot-templates' },
  { text: '用户管理', icon: <PeopleIcon />, path: '/users', superAdminOnly: true },
  {
    text: '场馆管理',
    icon: <BusinessIcon />,
    path: '/sites',
  },
  {
    text: 'Test',
    icon: <BugReportIcon />,
    path: '/test',
    superAdminOnly: true,
    devOnly: true,
  },
]

export default function MainWindow() {
  const [currentUser] = useCurrentUser()
  const { sites, currentSite, switchSite } = useSiteContext()
  const [drawerOpen, setDrawerOpen] = useState(true)
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  const userMenuOpen = Boolean(userMenuAnchor)
  const navigate = useNavigate()
  const location = useLocation()
  const [logoutMutation] = useMutation(UserLogoutDocument)
  const apolloClient = useApolloClient()

  // When user has no sites, only site management menu should be enabled
  const hasSites = sites.length > 0

  const isDevParam = new URLSearchParams(location.search).has('dev')

  // Filter out super admin only items when user is not super admin
  // Filter out devOnly items unless ?dev is in the URL and user is super_admin
  const visibleMenuItems = useMemo(
    () =>
      menuItems.filter((item) => {
        if (item.superAdminOnly && currentSite?.role !== SiteRole.SuperAdmin)
          return false
        if (item.devOnly && !isDevParam) return false
        return true
      }),
    [currentSite?.role, isDevParam],
  )

  // Redirect to site management page when user has no sites
  useEffect(() => {
    if (!hasSites && location.pathname !== '/sites') {
      navigate('/sites', { replace: true })
    }
  }, [hasSites, location.pathname, navigate])

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen)
  }

  const handleMenuItemClick = (path?: string, event?: React.MouseEvent) => {
    // Blur the button to prevent focus conflicts with dialogs
    if (event?.currentTarget) {
      ;(event.currentTarget as HTMLElement).blur()
    }

    if (path) {
      navigate(path)
    }
  }

  const handleLogout = async () => {
    setUserMenuAnchor(null) // Close menu

    try {
      const { data } = await logoutMutation()

      if (data?.logout.__typename === 'GraphqlRequestError') {
        console.error('Logout failed:', data.logout.message)
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Always clear local state regardless of mutation result
      sessionStorage.removeItem('authToken')
      await apolloClient.clearStore()
      // Force page reload to trigger LoginRedirector re-authentication check
      window.location.href = '/'
    }
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo/Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src={getAssetUrl('/logo.svg')} alt="Logo" style={{ height: 32 }} />
            <Typography variant="h6" component="div">
              几点几
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />

          {/* Right side - Site selector, Notifications and User */}
          {currentSite && (
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <PlaceIcon sx={{ fontSize: 20, mr: 0.5, color: 'text.secondary' }} />
              {sites.length > 1 ? (
                <Select
                  value={currentSite.id}
                  onChange={(e: SelectChangeEvent) => switchSite(e.target.value)}
                  size="small"
                  variant="standard"
                  disableUnderline
                  data-testid="site-selector"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Typography variant="body2" data-testid="site-name-display">
                  {currentSite.name}
                </Typography>
              )}
            </Box>
          )}

          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            aria-controls={userMenuOpen ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={userMenuOpen ? 'true' : undefined}
          >
            <Avatar
              sx={{ width: 32, height: 32 }}
              src={currentUser?.avatar ? getImageUrl(currentUser.avatar) : undefined}
            >
              {!currentUser?.avatar && <PersonIcon />}
            </Avatar>
            <Typography variant="body2">{currentUser?.name}</Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        id="user-menu"
        anchorEl={userMenuAnchor}
        open={userMenuOpen}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setUserMenuAnchor(null)
            navigate('/settings')
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>个人设置</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>退出登录</ListItemText>
        </MenuItem>
      </Menu>

      <Drawer
        variant="permanent"
        open={drawerOpen}
        sx={{
          width: drawerOpen ? drawerWidth : miniDrawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          '& .MuiDrawer-paper': {
            width: drawerOpen ? drawerWidth : miniDrawerWidth,
            boxSizing: 'border-box',
            mt: 8,
            height: 'calc(100% - 64px)',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        <List sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          {visibleMenuItems
            .filter((item) => !item.devOnly)
            .map((item) => {
              // Only site management menu is enabled when user has no sites
              const isDisabled = !hasSites && item.path !== '/sites'
              const isSelected = location.pathname === item.path

              return (
                <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={(e) => handleMenuItemClick(item.path, e)}
                    disabled={isDisabled}
                    selected={isSelected}
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{ opacity: drawerOpen ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
          <Box sx={{ flexGrow: 1 }} />
          {visibleMenuItems
            .filter((item) => item.devOnly)
            .map((item) => {
              const isSelected = location.pathname === item.path

              return (
                <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={(e) => handleMenuItemClick(item.path, e)}
                    selected={isSelected}
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 3 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{ opacity: drawerOpen ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}
        </List>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}
